import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { RUN_METRICS_BY, runMetrics } from '@mkcore/agents/schema';
import { jobMetrics } from '@mkcore/platform/schema';
import { addDays, today, zonedMidnight } from '@mkcore/shared';
import { Card, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { CountChart } from '@mkcore/ui/charts';
import { call } from '@/lib/api';
import { formatMicros } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { formatDuration } from '@/features/admin/job-status';
import { OverviewTiles } from '@/components/overview-tiles';

const search = z.object({ from: z.string().optional(), to: z.string().optional(), by: z.enum(RUN_METRICS_BY).optional() });
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/agents/insights')({ validateSearch: search, component: Insights });

const pct = (bp: number | null) => (bp === null ? '–' : `${(bp / 100).toLocaleString('nl-NL', { maximumFractionDigits: 1 })}%`);

/**
 * Jobs, agents and AI costs (CO-0975, CO-0853; docs/04-domeinen/agents.md, U9): runs per agent, revision, model,
 * trigger or day with how they ended, the success rate, the duration, the costs against the budget and the share of
 * drafts approved as they were; the jobs per type; and click-through to the failed runs. The hourly run alerts
 * (CO-0976) read the same actions.
 */
function Insights() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const canJobs = useCan('admin:read');
  const now = today();
  const from = s.from ?? `${now.slice(0, 7)}-01`;
  const to = s.to ?? now;
  const by = s.by ?? 'agent';
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const runs = useQuery({ queryKey: ['agent', 'run', 'metrics', from, to, by], queryFn: () => call(runMetrics, { from, to, by }) });
  const jobs = useQuery({
    queryKey: ['admin', 'job', 'metrics', from, to],
    queryFn: () => call(jobMetrics, { from: zonedMidnight(from).toISOString(), to: zonedMidnight(addDays(to, 1)).toISOString() }),
    enabled: canJobs,
  });
  const r = runs.data;
  // What people did with the drafts of these runs (CO-0853).
  const feedbackLine = (f: { approved: number; changed: number; revised: number; rejected: number }) => t('insights.feedbackLine', f);
  return (
    <>
      <PageHeader title={t('insights.title')} description={t('insights.description')} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('insights.from')} className="w-40">
          {(p) => <Input {...p} type="date" value={from} onChange={(e) => set({ from: e.target.value || undefined })} />}
        </Field>
        <Field label={t('insights.to')} className="w-40">
          {(p) => <Input {...p} type="date" value={to} onChange={(e) => set({ to: e.target.value || undefined })} />}
        </Field>
        <Field label={t('insights.by')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={by} onChange={(e) => set({ by: e.target.value as Search['by'] })}>
              {RUN_METRICS_BY.map((b) => (
                <option key={b} value={b}>
                  {t(`insights.byOption.${b}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      {runs.isError ? (
        <ErrorState title={t('errors.generic')} description={message(runs.error)} />
      ) : !r ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="grid gap-4">
          <OverviewTiles
            label={t('insights.totals')}
            tiles={[
              { label: t('insights.runs'), value: r.totals.runs },
              { label: t('insights.success'), value: pct(r.totals.successBp) },
              { label: t('insights.failed'), value: r.totals.failed, to: '/agents/runs', search: { status: 'failed' }, tone: r.totals.failed ? 'danger' : undefined },
              { label: t('insights.running'), value: r.totals.running },
              { label: t('insights.duration'), value: formatDuration(r.totals.medianMs), detail: t('insights.p90', { value: formatDuration(r.totals.p90Ms) }) },
              { label: t('insights.cost'), value: formatMicros(r.totals.costMicros, r.currency) },
              ...(r.totals.feedback ? [{ label: t('insights.accepted'), value: pct(r.totals.feedback.acceptedBp), detail: feedbackLine(r.totals.feedback) }] : []),
            ]}
          />
          {by === 'day' && r.items.length > 0 && (
            <Card className="p-4">
              <CountChart
                title={t('insights.perDay')}
                data={r.items.map((i) => ({ day: i.key, succeeded: i.succeeded, failed: i.failed }))}
                xKey="day"
                bars={[
                  { key: 'succeeded', name: t('insights.succeeded'), tone: 'success' },
                  { key: 'failed', name: t('insights.failed'), tone: 'danger' },
                ]}
              />
            </Card>
          )}
          {!r.items.length ? (
            <EmptyState title={t('insights.none')} />
          ) : (
            <Table aria-label={t('insights.runsTable')}>
              <THead>
                <Tr>
                  <Th>{t(`insights.byOption.${by}`)}</Th>
                  <Th className="text-right">{t('insights.runs')}</Th>
                  <Th className="text-right">{t('insights.failed')}</Th>
                  <Th className="text-right">{t('insights.success')}</Th>
                  <Th className="hidden text-right md:table-cell">{t('insights.median')}</Th>
                  <Th className="hidden text-right md:table-cell">{t('insights.p90Head')}</Th>
                  <Th className="text-right">{t('insights.cost')}</Th>
                  {by !== 'model' && <Th className="hidden text-right lg:table-cell">{t('insights.accepted')}</Th>}
                </Tr>
              </THead>
              <TBody>
                {r.items.map((i) => (
                  <Tr key={i.key}>
                    <Td className="font-bold">{i.label ?? t('insights.noAgent')}</Td>
                    <Td className="tabular text-right">{i.runs}</Td>
                    <Td className="tabular text-right">
                      {i.failed && by === 'agent' && i.key !== '-' ? (
                        <Link to="/agents/runs" search={{ status: 'failed', agentId: i.key }} className="text-danger hover:underline">
                          {i.failed}
                        </Link>
                      ) : (
                        i.failed
                      )}
                    </Td>
                    <Td className="tabular text-right">{pct(i.successBp)}</Td>
                    <Td className="hidden tabular text-right md:table-cell">{formatDuration(i.medianMs)}</Td>
                    <Td className="hidden tabular text-right md:table-cell">{formatDuration(i.p90Ms)}</Td>
                    <Td className="tabular text-right">
                      {formatMicros(i.costMicros, r.currency)}
                      {i.budgetMicros !== null && <span className="block text-xs text-fg-3">{t('insights.ofBudget', { budget: formatMicros(i.budgetMicros, r.currency) })}</span>}
                    </Td>
                    {i.feedback && (
                      <Td className="hidden tabular text-right lg:table-cell">
                        {pct(i.feedback.acceptedBp)}
                        <span className="block text-xs text-fg-3">{feedbackLine(i.feedback)}</span>
                      </Td>
                    )}
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </div>
      )}
      {canJobs && (
        <Card className="mt-6 grid gap-3 p-4">
          <h2 className="text-lg font-bold">{t('insights.jobs')}</h2>
          {jobs.isError ? (
            <ErrorState title={t('errors.generic')} description={message(jobs.error)} />
          ) : !jobs.data ? (
            <Skeleton className="h-32" />
          ) : !jobs.data.items.length ? (
            <EmptyState title={t('insights.noJobs')} />
          ) : (
            <Table aria-label={t('insights.jobs')}>
              <THead>
                <Tr>
                  <Th>{t('insights.jobType')}</Th>
                  <Th className="text-right">{t('insights.runs')}</Th>
                  <Th className="text-right">{t('insights.failed')}</Th>
                  <Th className="text-right">{t('insights.waiting')}</Th>
                  <Th className="hidden text-right md:table-cell">{t('insights.median')}</Th>
                  <Th className="hidden text-right md:table-cell">{t('insights.p90Head')}</Th>
                </Tr>
              </THead>
              <TBody>
                {jobs.data.items.map((j) => (
                  <Tr key={j.jobType}>
                    <Td className="font-mono text-sm">{j.jobType}</Td>
                    <Td className="tabular text-right">{j.runs}</Td>
                    <Td className="tabular text-right">
                      {j.openFailures ? (
                        <Link to="/admin/jobs" search={{ jobType: j.jobType, openFailures: true } as never} className="text-danger hover:underline">
                          {t('insights.openFailures', { failed: j.failed, open: j.openFailures })}
                        </Link>
                      ) : (
                        j.failed
                      )}
                    </Td>
                    <Td className="tabular text-right">{j.waiting + j.running + j.paused}</Td>
                    <Td className="hidden tabular text-right md:table-cell">{formatDuration(j.medianMs)}</Td>
                    <Td className="hidden tabular text-right md:table-cell">{formatDuration(j.p90Ms)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      )}
    </>
  );
}
